import {Box, Card, Divider, Stack, Typography} from '@mui/material';
import React, {Fragment} from 'react';

import {NetworkEnum} from 'main/analytics/gaTagsEnum';
import {Snackbar, TagChip} from 'presentation/components';
import {PhonesDrawer} from 'presentation/components/PhonesDrawer';
import {PhonesMenu} from 'presentation/components/PhonesMenu';
import {formatText} from 'presentation/utils';

import Icon from 'presentation/icons';
import {QualificationList} from 'presentation/modules/guide/shared/ui/components';
import {RenderAddressField} from 'presentation/modules/guide/shared/ui/components/RenderAddressField';
import {translations} from 'presentation/translations-new';
import {useHealthGuideCard} from './hooks/useHealthGuideCard';
import {StyledSmallButton, StyledTypographyTitle} from './styles';
import {HealthGuideCardProps} from './types';
import {SimpleConfirmationDrawer} from 'presentation/pages/private/booking/components/ConfirmationDrawer/SimpleConfirmationDrawer';
import {PendingDialog} from 'presentation/modules/booking/shared/ui/components/PendingDialog';

const {showDetails, call, fromMe, referenceNetwork, loading} = translations['pt-br'].commons;

const {newAppointment} = translations['pt-br'].privateFlow.myAppointments;
const {
	unitDetails: {emptyPhone, choosePhoneNumber},
	cityReference,
} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow;

export function HealthGuideCard({
	provider,
	showTradeName,
	handleOpenDetails,
}: HealthGuideCardProps) {
	const {
		handleOpenPhoneMenu,
		primaryName,
		shouldShowQualifications,
		theme,
		address,
		shouldShowCallButton,
		shouldShowAppointmentButton,
		shouldShowExamButton,
		phoneNumbers,
		isPhoneNumberMenuOpen,
		setAnchorEl,
		showPhonesDrawer,
		openPhoneDrawerState,
		setOpenPhoneDrawerState,
		hasPhoneNumbers,
		showEmptyPhoneSnackbar,
		setShowEmptyPhoneSnackbar,
		cityName,
		coordinates,
		isTelemedicine,
		anchorEl,
		isPending,
		openConfirmationDrawer,
		handleCloseConfirmationDrawer,
		handleOpenConfirmationDrawer,
		handleConfirmAndContinue,
		handleSelectAppointment,
		handleSelectExam,
		isSchedulingByMedicalGuideEnabled,
		handleNavigateToBookingFlow,
	} = useHealthGuideCard({provider, showTradeName});

	return (
		<Fragment>
			<Card elevation={0}>
				<Stack height="100%" justifyContent="space-between">
					<Stack p={2} gap={1}>
						<Stack gap={1}>
							{provider?.ordemRR ? (
								<TagChip
									label={referenceNetwork}
									color="success"
									sx={{
										width: 'fit-content',
										fontSize: '0.75rem',
										borderRadius: '4px',
									}}
								/>
							) : null}
							<StyledTypographyTitle variant="subtitle1">
								{formatText(primaryName)}
							</StyledTypographyTitle>

							{shouldShowQualifications ? (
								<QualificationList qualifications={provider?.qualificacoes} />
							) : null}
						</Stack>
						<Stack gap={2}>
							<RenderAddressField
								formattedAddress={address?.formattedAddress}
								addressForMaps={address?.addressForMaps}
							/>
						</Stack>
						{provider?.distancia > 0 ? (
							<Stack direction="row" gap={1}>
								<Stack minWidth={20}>
									<Icon
										name={'location-on-filled'}
										size={20}
										color={theme.palette.primary.main}
									/>
								</Stack>
								<Typography variant="body2" color="text.secondary">
									{provider.distancia?.toFixed(1)} km{' '}
									{coordinates
										? fromMe
										: `${cityReference} ${formatText(cityName)}`}
								</Typography>
							</Stack>
						) : undefined}
					</Stack>
					<Box>
						<Divider />
						<Stack
							direction="row"
							gap={1}
							justifyContent="flex-end"
							alignItems="initial"
							py={1}
							px={2}>
							{!isTelemedicine ? (
								<StyledSmallButton
									onClick={handleOpenDetails}
									size="small"
									variant="outlined"
									data-tag-id={NetworkEnum.CLICK_VER_DETALHES}>
									{showDetails}
								</StyledSmallButton>
							) : null}

							{shouldShowCallButton ? (
								<StyledSmallButton
									onClick={handleOpenPhoneMenu}
									variant="contained"
									size="small"
									data-tag-id={NetworkEnum.CLICK_VER_CONTATO_REDE_CREDENCIADA}>
									{call}
								</StyledSmallButton>
							) : null}

							{shouldShowAppointmentButton ? (
								<StyledSmallButton
									onClick={
										isSchedulingByMedicalGuideEnabled
											? handleConfirmAndContinue
											: handleSelectAppointment
									}
									size="small"
									variant="contained"
									data-tag-id={
										isTelemedicine
											? NetworkEnum.CLICK_GUIA_AGENDAR_TELECONSULTA
											: NetworkEnum.CLICK_GUIA_AGENDAR_CONSULTA
									}>
									{newAppointment}
								</StyledSmallButton>
							) : null}

							{shouldShowExamButton ? (
								<StyledSmallButton
									onClick={
										isSchedulingByMedicalGuideEnabled
											? handleConfirmAndContinue
											: handleSelectExam
									}
									size="small"
									variant="contained"
									data-tag-id={
										isTelemedicine
											? NetworkEnum.CLICK_GUIA_AGENDAR_TELECONSULTA
											: NetworkEnum.CLICK_GUIA_AGENDAR_CONSULTA
									}>
									{newAppointment}
								</StyledSmallButton>
							) : null}
						</Stack>
					</Box>
				</Stack>
			</Card>

			{hasPhoneNumbers ? (
				<PhonesMenu.Root
					open={isPhoneNumberMenuOpen}
					onClose={() => setAnchorEl(null)}
					anchorEl={anchorEl}>
					<PhonesMenu.Simple phoneNumbers={phoneNumbers} />
				</PhonesMenu.Root>
			) : (
				<Snackbar
					onClose={() => setShowEmptyPhoneSnackbar(false)}
					message={emptyPhone}
					open={showEmptyPhoneSnackbar}
				/>
			)}

			{showPhonesDrawer ? (
				<PhonesDrawer.Root
					open={openPhoneDrawerState}
					toggleDrawer={() => setOpenPhoneDrawerState(prev => !prev)}
					title={choosePhoneNumber}>
					<PhonesDrawer.Simple phoneNumbers={phoneNumbers} />
				</PhonesDrawer.Root>
			) : null}

			<SimpleConfirmationDrawer
				isOpened={openConfirmationDrawer}
				handleClose={handleCloseConfirmationDrawer}
				handleOpen={handleOpenConfirmationDrawer}
				handleConfirmAndContinue={handleNavigateToBookingFlow}
			/>

			<PendingDialog open={isPending} text={loading} />
		</Fragment>
	);
}
